import { Box, Typography } from '@mui/material';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import CustomSearchbar from 'components/CustomSearchBar';

import { useAppSelector } from 'redux-store';

import DeviceGroup from './DeviceGroup';

interface PairDeviceGroupsProps {
  selectedDeviceGroupIds: number[];
  setSelectedDeviceGroupIds: (selectedDeviceGroupIds: number[]) => void;
}

const PairDeviceGroup: FC<PairDeviceGroupsProps> = ({
  selectedDeviceGroupIds,
  setSelectedDeviceGroupIds,
}) => {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);

  const deviceGroups = useAppSelector(s => s.deviceGroups.items);

  const fuse = useMemo(
    () =>
      new Fuse(deviceGroups, {
        keys: ['name'],
        threshold: 0.4,
      }),
    [deviceGroups],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.name));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  const filteredDeviceGroups = useMemo(
    () =>
      searchResults
        ? deviceGroups.filter(group => searchResults.includes(group.name))
        : deviceGroups,
    [deviceGroups, searchResults],
  );

  return (
    <Box>
      <Box mb={3} width="100%">
        <CustomSearchbar
          search={searchString}
          setSearch={setSearchString}
          hasSearched={searchString.length > 0}
          handleSearch={handleSearch}
          handleClearSearch={handleClearSearch}
        />
      </Box>
      {filteredDeviceGroups.length > 0 ? (
        <Box>
          {filteredDeviceGroups.map(item => (
            <DeviceGroup
              key={item.id}
              id={item.id}
              groupName={item.name}
              devices={item.devices}
              selectedDeviceGroupIds={selectedDeviceGroupIds}
              setSelectedDeviceGroupIds={setSelectedDeviceGroupIds}
            />
          ))}
        </Box>
      ) : (
        <Typography variant="body1" textAlign="center">
          {t('common:pages.rfids.pair_devices.no_devices_groups_found')}
        </Typography>
      )}
    </Box>
  );
};

export default PairDeviceGroup;
